/**
 * @fileoverview The picture as it will be once saved: turned at once when Rotate is
 * pressed, with the crop box over it when there is one.
 *
 * The picture is drawn in a box the shape of the turned picture, as large as
 * the room allows with a little margin, so the crop box's corners can be
 * grabbed even at the picture's edge. The stored picture is not touched: it
 * is an <img> turned with CSS, and the crop box is drawn over it in
 * fractions of the turned picture, which is what the server is sent.
 *
 * Dragging a corner resizes the box (the opposite corner stays put); dragging
 * inside it moves it. Outside the box the picture is dimmed.
 *
 * Where: EditPhotoDialog and ImageEditDialog, as the picture.
 * API: none.
 * Settings: none.
 * Words (en/es): plants.photo.edit.cropArea.
 *
 * @module ImageEditStage
 */

import React from 'react';
import { useTranslation } from '@/shared/hooks/useTranslation';
import { dragCrop, type CropBox, type CropHandle } from '@/shared/lib/imageEdit';
import type { ImageEditState } from './useImageEdit';

/** Room left round the picture, in pixels, so a corner handle at its edge is not cut off. */
const STAGE_MARGIN_PX = 14;

/** The four corners, and where each handle sits on the box. */
const CORNERS: ReadonlyArray<{ handle: Exclude<CropHandle, 'move'>; place: string; mark: string }> = [
  { handle: 'nw', place: '-left-[22px] -top-[22px] cursor-nwse-resize', mark: 'left-[18px] top-[18px] border-l-4 border-t-4' },
  { handle: 'ne', place: '-right-[22px] -top-[22px] cursor-nesw-resize', mark: 'right-[18px] top-[18px] border-r-4 border-t-4' },
  { handle: 'sw', place: '-bottom-[22px] -left-[22px] cursor-nesw-resize', mark: 'bottom-[18px] left-[18px] border-b-4 border-l-4' },
  { handle: 'se', place: '-bottom-[22px] -right-[22px] cursor-nwse-resize', mark: 'bottom-[18px] right-[18px] border-b-4 border-r-4' },
];

/** What the stage draws and who it tells. */
interface ImageEditStageProps {
  /** The stored picture. */
  src: string;
  /** What it is of, for a screen reader. */
  alt: string;
  /** The edit in progress (useImageEdit). */
  state: ImageEditState;
  /** Called when the picture has loaded, or failed to. */
  onLoad?: () => void;
  /** No dragging while the edit is being saved. */
  disabled?: boolean;
}

/**
 * The turned picture, filling its container, with the crop box over it.
 *
 * @param props - ImageEditStageProps.
 */
export const ImageEditStage: React.FC<ImageEditStageProps> = ({ src, alt, state, onLoad, disabled }) => {
  /** The room the stage fills, measured to size the picture's box. */
  const frameRef = React.useRef<HTMLDivElement>(null);
  const [frame, setFrame] = React.useState({ width: 0, height: 0 });

  // The room there is, followed as the dialog and the keyboard change it.
  React.useLayoutEffect(() => {
    const node = frameRef.current;
    if (!node) return undefined;
    /** Reads the frame's size now. */
    const measure = (): void => setFrame({ width: node.clientWidth, height: node.clientHeight });
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  const { edit, aspect, setNatural } = state;
  const roomWidth = Math.max(0, frame.width - 2 * STAGE_MARGIN_PX);
  const roomHeight = Math.max(0, frame.height - 2 * STAGE_MARGIN_PX);
  const boxWidth = Math.min(roomWidth, roomHeight * aspect);
  const boxHeight = aspect ? boxWidth / aspect : 0;
  const sideways = edit.rotate % 180 !== 0;

  return (
    <div ref={frameRef} className="absolute inset-0 flex items-center justify-center">
      <div className="relative" style={{ width: boxWidth, height: boxHeight }}>
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={src}
            alt={alt}
            draggable={false}
            onLoad={(event) => {
              setNatural(event.currentTarget.naturalWidth, event.currentTarget.naturalHeight);
              onLoad?.();
            }}
            onError={onLoad}
            className="absolute left-1/2 top-1/2 max-w-none select-none"
            style={{
              width: sideways ? boxHeight : boxWidth,
              height: sideways ? boxWidth : boxHeight,
              transform: `translate(-50%, -50%) rotate(${edit.rotate}deg)`,
            }}
          />
          {/* The dimming outside the box, clipped to the picture. */}
          {edit.crop && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute"
              style={{ ...boxStyle(edit.crop), boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.55)' }}
            />
          )}
        </div>
        {edit.crop && boxWidth > 0 && (
          <CropFrame crop={edit.crop} width={boxWidth} height={boxHeight} state={state} disabled={disabled} />
        )}
      </div>
    </div>
  );
};

/**
 * Where a crop box sits, as percentages of the picture.
 *
 * @param crop - The box, in fractions.
 */
function boxStyle(crop: CropBox): React.CSSProperties {
  return {
    left: `${crop.x * 100}%`,
    top: `${crop.y * 100}%`,
    width: `${crop.width * 100}%`,
    height: `${crop.height * 100}%`,
  };
}

/** The crop box's outline and corners, and the drag behind them. */
interface CropFrameProps {
  /** The box, in fractions of the turned picture. */
  crop: CropBox;
  /** The drawn picture's width in pixels, which a drag is measured against. */
  width: number;
  /** Its height in pixels. */
  height: number;
  /** The edit in progress, which a drag changes. */
  state: ImageEditState;
  /** No dragging while saving. */
  disabled?: boolean;
}

/**
 * The box itself: drag inside to move it, a corner to size it.
 *
 * @param props - CropFrameProps.
 */
const CropFrame: React.FC<CropFrameProps> = ({ crop, width, height, state, disabled }) => {
  const { reactTranslate: t } = useTranslation();
  /** The drag under way: what is dragged, where it began, and the box then. */
  const drag = React.useRef<{ handle: CropHandle; x: number; y: number; start: CropBox } | null>(null);
  /** The box, which catches the pointer for the length of a drag. */
  const boxRef = React.useRef<HTMLDivElement>(null);

  /**
   * Starts a drag of a corner or of the whole box. The box catches the
   * pointer, so the drag carries on wherever the finger goes.
   *
   * @param handle - What is being dragged.
   */
  const begin = (handle: CropHandle) => (event: React.PointerEvent<HTMLElement>): void => {
    if (disabled) return;
    event.preventDefault();
    event.stopPropagation();
    // A pointer the browser no longer knows (lifted already) cannot be caught; the drag still works while it is over the box.
    try { boxRef.current?.setPointerCapture(event.pointerId); } catch { /* not capturable */ }
    drag.current = { handle, x: event.clientX, y: event.clientY, start: crop };
  };

  /** Follows the pointer while a drag is on. */
  const move = (event: React.PointerEvent<HTMLElement>): void => {
    const current = drag.current;
    if (!current || !width || !height) return;
    const dx = (event.clientX - current.x) / width;
    const dy = (event.clientY - current.y) / height;
    state.setCrop(dragCrop(current.start, current.handle, dx, dy, state.shape, state.aspect));
  };

  /** Ends the drag. */
  const end = (): void => { drag.current = null; };

  return (
    <div
      ref={boxRef}
      role="group"
      aria-label={t('plants.photo.edit.cropArea')}
      className="absolute cursor-move touch-none border-2 border-white/90"
      style={boxStyle(crop)}
      onPointerDown={begin('move')}
      onPointerMove={move}
      onPointerUp={end}
      onPointerCancel={end}
    >
      {CORNERS.map(({ handle, place, mark }) => (
        <span
          key={handle}
          aria-hidden="true"
          onPointerDown={begin(handle)}
          className={`absolute h-11 w-11 ${place}`}
        >
          <span className={`absolute h-4 w-4 border-white ${mark}`} />
        </span>
      ))}
    </div>
  );
};
